import React, { useEffect, useState } from "react";
import { Flex } from "./Flex";
import styled from "styled-components";
import VerificationInput from "react-verification-input";
import { FontSize, Text } from "./Text";
import { Rounded } from "../theme/Rounded";
import { Spacing } from "../theme/Spacing";
import { Button } from "./Button";
import { Trans, useTranslation } from "react-i18next";
import { AtiraToast } from "../utils/AtiraToast";
import { Spinner } from "./Spinner";
import { Timer } from "../utils/local-antd";

const resendTokenTimeout = 1000 * 150;

const VerificationInputWrapper = styled(Flex)<{ valid?: boolean }>`
  flex-direction: column;
  width: 100%;
  gap: ${Spacing.s};

  .vi__container {
    width: 100%;
  }

  .vi__character--selected {
    color: ${({ theme }) => theme.main} !important;
    outline-color: ${({ theme }) => theme.main} !important;
    border-color: ${({ theme }) => theme.main} !important;
  }

  .vi__character:not(.vi__character--filled) {
    color: ${({ theme }) => theme.darkSub};
    border-color: ${({ theme }) => theme.darkSub} !important;
  }

  .vi__character {
    border-radius: ${Rounded.md} !important;
    border: ${({ theme, valid }) =>
      `1px solid ${theme[valid ? "darkSub" : "danger"]}`};
  }

  .vi__character--inactive {
    background-color: ${({ theme }) => theme.sub} !important;
    color: ${({ theme }) => theme.darkerSub};
  }
`;

const StyledButton = styled(Button)<{ isLoading?: boolean }>`
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  margin: 0;
  padding: 0;
  align-self: flex-start;
  text-decoration: underline;
  text-underline-offset: 4px;
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  cursor: ${({ isLoading, disabled }) =>
    isLoading || disabled ? "not-allowed" : "pointer"};
  flex-shrink: 0;

  & > span:first-of-type {
    opacity: ${({ isLoading }) => (isLoading ? 0.7 : 1)};
  }
`;

const TimerText = styled(Text)`
  font-size: ${FontSize.xs};

  .ant-statistic {
    display: inline-block !important;
  }

  .ant-statistic-content-value {
    font-size: ${FontSize.xs};
    display: block !important;
  }
`;

type AtiraVerificationInputProps = {
  value?: string;
  onChange: ATVoidFunction<string>;
  valid?: boolean;
  errorMessage?: string;
  /** Omit to hide the resend link (e.g. authenticator-app codes). */
  onResendToken?: () => Promise<void>;
  /**
   * sessionStorage key under which the resend-cooldown deadline is persisted, so
   * a page refresh resumes the countdown instead of restarting it. Omit for the
   * previous in-memory-only behaviour.
   */
  cooldownStorageKey?: string;
};

const readStoredDeadline = (key?: string): number | null => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const AtiraVerificationInput: React.FC<AtiraVerificationInputProps> = ({
  valid = true,
  value,
  onChange,
  errorMessage,
  onResendToken,
  cooldownStorageKey,
}) => {
  // Epoch-ms deadline the resend button stays disabled until. First mount
  // assumes a code was just sent; a refresh restores whatever is left.
  const [cooldownUntil, setCooldownUntil] = useState<number>(
    () =>
      readStoredDeadline(cooldownStorageKey) ?? Date.now() + resendTokenTimeout,
  );
  const [resendTokenDisabled, setResendTokenDisabled] = useState(
    () => cooldownUntil > Date.now(),
  );
  const [resendTokenLoading, setResendTokenLoading] = useState(false);
  // Bumped on every resend so the countdown <Timer> remounts on the new deadline.
  const [resendCount, setResendCount] = useState(0);

  const { t } = useTranslation(undefined, { keyPrefix: "common" });

  // Keep the persisted deadline in step with the live cooldown.
  useEffect(() => {
    if (!cooldownStorageKey) return;
    try {
      if (resendTokenDisabled) {
        sessionStorage.setItem(cooldownStorageKey, String(cooldownUntil));
      } else {
        sessionStorage.removeItem(cooldownStorageKey);
      }
    } catch {
      // ignore - worst case the countdown doesn't survive a refresh
    }
  }, [cooldownStorageKey, cooldownUntil, resendTokenDisabled]);

  const localResendToken = async () => {
    if (!onResendToken || resendTokenDisabled || resendTokenLoading) {
      return;
    }

    try {
      setResendTokenLoading(true);

      await onResendToken();

      // Re-arm the cooldown: keep the button visible but disabled until the
      // timer runs down again, so the user can resend more than once.
      setCooldownUntil(Date.now() + resendTokenTimeout);
      setResendTokenDisabled(true);
      setResendCount((c) => c + 1);

      AtiraToast.success(t("resendVerificationCode.success"));
    } catch (e) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setResendTokenLoading(false);
    }
  };

  return (
    <VerificationInputWrapper valid={valid}>
      <VerificationInput
        placeholder="-"
        onChange={onChange}
        value={value}
        validChars="0-9"
        inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
        containerProps={{ dir: "ltr" }}
      />

      {valid === false && errorMessage ? (
        <Text color="danger" fontSize="s">
          {errorMessage}
        </Text>
      ) : null}

      {onResendToken ? (
        <Flex flexDirection="column" gap="xs">
          <StyledButton
            onClick={localResendToken}
            disabled={resendTokenDisabled}
            isLoading={resendTokenLoading}
          >
            <span>{t("resendVerificationCode")}</span>
            {resendTokenLoading ? <Spinner color="main" /> : null}
          </StyledButton>

          {resendTokenDisabled ? (
            <TimerText>
              <Trans
                i18nKey="common.resendVerificationCode.enabledIn"
                components={[
                  <Timer
                    key={resendCount}
                    type="countdown"
                    value={cooldownUntil}
                    format="mm:ss"
                    onFinish={() => setResendTokenDisabled(false)}
                  />,
                ]}
              />
            </TimerText>
          ) : null}
        </Flex>
      ) : null}
    </VerificationInputWrapper>
  );
};
